import { mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import { fromNowUtc, formatUtc } from '@/utils/time'
import { buildDashboardLoginUrl } from '@/utils/dashboardLoginUrl'

/**
 * Shared core for the client quick-action surfaces (floating widget and page
 * header strip): loads the client, runs the synchronous full-static generate
 * with a live seconds counter, and builds the Dashboard / Website URLs.
 */
export default {
	props: {
		clientId: { type: [Number, String], required: true },
	},
	data() {
		return {
			client: null,
			generating: false,
			seconds: 0,
			timer: null,
			justFinished: false,
			lastElapsed: 0,
			finishedTimer: null,
			dashboardUrl: process.env.VUE_APP_DASHBOARD_URL,
		}
	},
	computed: {
		...mapState(['authUser', 'tokenJuniper']),
		lastStaticHuman() {
			if (!this.client || !this.client.last_static_generated_at) return ''
			return fromNowUtc(this.client.last_static_generated_at)
		},
		lastStaticFull() {
			if (!this.client || !this.client.last_static_generated_at) return ''
			return formatUtc(this.client.last_static_generated_at)
		},
		websiteUrl() {
			if (!this.client) return '#'
			return this.client.staging ? this.client.staging_url : this.client.url
		},
		isUnified() {
			return !!this.client && this.client.dashboard === 'unified'
		},
		// The page may be the upgrade copy itself (upgrade_id set): its
		// dashboard link then opens the editors on the copy, and the title says so.
		isUpgradeCopy() {
			return !!this.client && !!this.client.upgrade_id
		},
		dashboardLoginTitle() {
			const base = this.isUnified ? 'Login to Unified Dashboard' : 'Login to Dashboard'
			return this.isUpgradeCopy ? `${base} (upgrade copy)` : base
		},
		dashboardLoginUrl() {
			if (!this.client) return '#'
			// Same routing as the clients table: unified dashboards go straight
			// to app.heygov.com, everything else through the TW dashboard login.
			return buildDashboardLoginUrl(this.client, {
				dashboardUrl: this.dashboardUrl,
				email: this.authUser ? this.authUser.email : '',
				tokenJuniper: this.tokenJuniper,
			})
		},
	},
	watch: {
		clientId: {
			immediate: true,
			handler() {
				this.client = null
				this.getClient()
			},
		},
	},
	beforeDestroy() {
		// The build keeps running server-side; only the local counters stop.
		if (this.timer) clearInterval(this.timer)
		if (this.finishedTimer) clearTimeout(this.finishedTimer)
	},
	methods: {
		getClient() {
			juniperApi
				.get('/clients/' + this.clientId)
				.then(({ data }) => {
					this.client = data
				})
				.catch(error => {
					console.error('Quick actions: client load failed', error)
				})
		},

		generateStatic() {
			if (this.generating || !this.client) return
			this.generating = true
			this.justFinished = false
			this.seconds = 0
			this.timer = setInterval(() => {
				this.seconds++
			}, 1000)

			juniperApi
				.post('/clients/' + this.clientId + '/static/generate')
				.then(() => {
					this.lastElapsed = this.seconds
					this.stopTimer()
					if (this.client) this.client.last_static_generated_at = new Date().toISOString()
					this.justFinished = true
					this.finishedTimer = setTimeout(() => {
						this.justFinished = false
					}, 6000)
					// Let open views (e.g. ClientEdit's "Last static" card) refresh
					// their own copies of the client / deployments data.
					this.$root.$emit('juniper-static-generated', String(this.clientId))
				})
				.catch(error => {
					this.stopTimer()
					this.$swal.fire({
						icon: 'warning',
						title: 'Static generation failed',
						text: (error.response && error.response.data && error.response.data.message) || error.message,
					})
				})
		},

		stopTimer() {
			if (this.timer) clearInterval(this.timer)
			this.timer = null
			this.generating = false
		},
	},
}
